import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ProjectApi} from "../../api/authority/generated/index";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    buildChannelRow,
    ChannelMixColors,
    channelRowName,
    dividePercent,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartMultiplier,
    formatChartNumber,
    hasMetricValue,
    KpiCard,
    MetricBasis,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    currencyUnit,
    ReportingPageDescription,
    snapshotCompletenessAlert,
    SortableHeader,
    useSortableRows,
} from "./ReportingLab.jsx";

function marketingTableValue(row, key) {
    return {
        channel: row.channel?.name,
        marketing: row.spendMarketing,
        revenue: row.turnover,
        profit: row.profit,
        pno: row.pno,
        roas: row.roas,
        poas: row.poas,
        clicks: row.clicks,
        impressions: row.impressions,
        ctr: row.ctr,
        cpc: row.cpc,
        cpm: row.cpm,
        costPerOrder: row.costPerOrder,
        aov: row.averageOrderValue,
        conversion: row.clickConversionRate,
        orders: row.orders,
    }[key];
}

function MarketingSystemCard({row, rows, color, renderCurrencyValue, renderNumber, renderPercentValue, renderRatioValue, hasPoas}) {
    const {t} = useTranslation();

    return <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div className="mb-3 h-1 rounded-full" style={{backgroundColor: color}}/>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t("reporting_lab.marketing.ad_system", {defaultValue: "Reklamní systém"})}</div>
        <div className="mt-1 text-lg font-bold text-slate-900 dark:text-slate-100">{channelRowName(row, rows)}</div>
        <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm xl:grid-cols-3">
            <div>
                <div className="text-xs uppercase text-slate-400">{t("reporting_lab.marketing.costs", {defaultValue: "Náklady"})}</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">{renderCurrencyValue(row.spendMarketing, true)}</div>
            </div>
            <div>
                <div className="text-xs uppercase text-slate-400">{t("reporting_lab.kpi.attributed_revenue", {defaultValue: "Přiřazený obrat"})}</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">{renderCurrencyValue(row.turnover, true)}</div>
            </div>
            <div>
                <div className="text-xs uppercase text-slate-400">{t("reporting_lab.kpi.orders", {defaultValue: "Objednávky"})}</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">{renderNumber(row.orders)}</div>
            </div>
            <div>
                <div className="text-xs uppercase text-slate-400">PNO</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">{renderPercentValue(row.pno)}</div>
            </div>
            <div>
                <div className="text-xs uppercase text-slate-400">{hasPoas ? "ROAS / POAS" : "ROAS"}</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">{renderRatioValue(row.roas)}{hasPoas ? ` / ${renderRatioValue(row.poas)}` : ""}</div>
            </div>
        </div>
    </div>;
}

export default function ReportingMarketingLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const renderRatioValue = (value) => formatChartMultiplier(value);
    const [channelData, setChannelData] = useState([]);
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setChannelData([]);
        setSnapshot(null);

        const projectApi = new ProjectApi(getAuthorityConfiguration());
        projectApi.marketingChannelOverviews({projectId: project.id, erpFilter})
            .then((result) => {
                if (!active) {
                    return;
                }

                setChannelData((result.items ?? []).map((item) => ({
                    channel: {id: item.id, name: item.name},
                    overview: item.overview,
                })));
                setSnapshot(result.snapshot);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, erpFilter]);

    const channelRows = useMemo(() => {
        return channelData
            .map(({channel, overview}) => buildChannelRow(channel, overview, MetricBasis.Marketing))
            .filter((row) => moneyAmount(row.spendMarketing) > 0 || moneyAmount(row.turnover) > 0 || row.orders > 0)
            .sort((a, b) => moneyAmount(b.spendMarketing) - moneyAmount(a.spendMarketing));
    }, [channelData]);
    const {sortedRows: sortedTableRows, sort: tableSort, toggleSort} = useSortableRows(
        channelRows,
        "marketing",
        marketingTableValue,
    );
    const hasProfit = channelRows.some((row) => hasMetricValue(row.profit));
    const hasPoas = channelRows.some((row) => hasMetricValue(row.poas));

    const totals = useMemo(() => {
        return channelRows.reduce((accumulator, row) => {
            accumulator.spend += moneyAmount(row.spendMarketing);
            accumulator.turnover += moneyAmount(row.turnover);
            accumulator.profit += moneyAmount(row.profit);
            accumulator.orders += row.orders;
            accumulator.clicks += row.clicks;
            accumulator.impressions += row.impressions;
            return accumulator;
        }, {spend: 0, turnover: 0, profit: 0, orders: 0, clicks: 0, impressions: 0});
    }, [channelRows]);

    const currency = channelRows.find((row) => row.spendMarketing?.currency)?.spendMarketing?.currency ?? projectCurrency(project);
    const spendMoney = {amount: totals.spend, currency};
    const turnoverMoney = {amount: totals.turnover, currency};
    const roas = dividePercent(totals.turnover, totals.spend);
    const poas = dividePercent(totals.profit, totals.spend);
    const pno = dividePercent(totals.spend, totals.turnover);
    const cpa = totals.orders ? {amount: totals.spend / totals.orders, currency} : null;
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const marketingComparisonOption = {
        color: ["#7c3aed", "#2563eb", "#16a34a", "#111827"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 38, bottom: 58, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, [
                (value) => formatChartCurrency(value, currency),
                (value) => formatChartCurrency(value, currency),
                (value) => formatChartCurrency(value, currency),
                formatChartMultiplier,
            ]),
        },
        legend: {bottom: 4, ...chartLegendStyle},
        xAxis: {type: "category", data: channelRows.slice(0, 8).map((row) => channelRowName(row, channelRows))},
        yAxis: [
            {
                type: "value",
                name: currencyUnit(currency),
                axisLabel: {
                    fontSize: 12,
                    formatter: formatChartNumber,
                },
            },
            {
                type: "value",
                name: "ROAS",
                axisLabel: {
                    fontSize: 12,
                    formatter: formatChartMultiplier,
                },
            },
        ],
        series: [
            {
                name: t("reporting_lab.series.marketing", {defaultValue: "Marketing"}),
                type: "bar",
                data: channelRows.slice(0, 8).map((row) => moneyToMajor(row.spendMarketing)),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.series.revenue", {defaultValue: "Obrat"}),
                type: "bar",
                data: channelRows.slice(0, 8).map((row) => moneyToMajor(row.turnover)),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.series.profit", {defaultValue: "Marže"}),
                type: "bar",
                data: channelRows.slice(0, 8).map((row) => moneyToMajor(row.profit)),
                barMaxWidth: 28,
            },
            {
                name: "ROAS",
                type: "line",
                yAxisIndex: 1,
                smooth: true,
                symbolSize: 7,
                data: channelRows.slice(0, 8).map((row) => row.roas ?? 0),
            },
        ].filter((series) => hasProfit || series.name !== t("reporting_lab.series.profit", {defaultValue: "Marže"})),
    };

    const tableColumns = [
        {key: "channel", label: t("reporting_lab.table.channel", {defaultValue: "Kanál"}), render: (row) => channelRowName(row, channelRows), align: "left"},
        {key: "marketing", label: t("reporting_lab.table.marketing", {defaultValue: "Marketing"}), render: (row) => renderCurrencyValue(row.spendMarketing, true)},
        {key: "revenue", label: t("reporting_lab.table.revenue", {defaultValue: "Obrat"}), render: (row) => renderCurrencyValue(row.turnover, true)},
        {key: "profit", label: t("reporting_lab.table.contribution", {defaultValue: "Marže"}), tooltip: t("reporting_lab.table.contribution_help", {defaultValue: "Marže = obrat přiřazených objednávek minus nákupní cena zboží a marketingové náklady. Nejde o čistý účetní zisk; nezahrnuje interní náklady dopravy, plateb ani režii."}), render: (row) => renderCurrencyValue(row.profit, true)},
        {key: "pno", label: "PNO", render: (row) => renderPercentValue(row.pno)},
        {key: "roas", label: "ROAS", render: (row) => renderRatioValue(row.roas)},
        {key: "poas", label: "POAS", render: (row) => renderRatioValue(row.poas)},
        {key: "clicks", label: t("reporting_lab.table.clicks", {defaultValue: "Kliky"}), render: (row) => renderNumber(row.clicks)},
        {key: "impressions", label: t("reporting_lab.table.impressions", {defaultValue: "Imprese"}), render: (row) => renderNumber(row.impressions)},
        {key: "ctr", label: "CTR", render: (row) => renderPercentValue(row.ctr)},
        {key: "cpc", label: "CPC", render: (row) => renderCurrencyValue(row.cpc, true)},
        {key: "cpm", label: "CPM", render: (row) => renderCurrencyValue(row.cpm, true)},
        {key: "costPerOrder", label: t("reporting_lab.table.cost_per_order", {defaultValue: "Cena / obj."}), render: (row) => renderCurrencyValue(row.costPerOrder, true)},
        {key: "aov", label: t("reporting_lab.table.aov", {defaultValue: "AOV"}), render: (row) => renderCurrencyValue(row.averageOrderValue, true)},
        {key: "conversion", label: t("reporting_lab.table.conversion_rate", {defaultValue: "Konverze"}), render: (row) => renderPercentValue(row.clickConversionRate)},
        {key: "orders", label: t("reporting_lab.table.orders_short", {defaultValue: "Obj."}), render: (row) => renderNumber(row.orders)},
    ].filter((column) => (hasProfit || column.key !== "profit") && (hasPoas || column.key !== "poas"));

    return <WideSecureLayout
        title={t("reporting_lab.pages.marketing", {defaultValue: "Marketing"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[snapshotCompletenessAlert(snapshot, t)]}
    >
        <div className="grid grid-cols-12 gap-4">
                <ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.marketing.title", {defaultValue: "K čemu slouží marketingový přehled"})}
                >
                    {hasPoas
                        ? t("reporting_lab.descriptions.marketing.text", {defaultValue: "Souhrn placených i neplacených kanálů: náklady, hodnota objednávek, ROAS, POAS, PNO a rozpad výkonu podle reklamních systémů."})
                        : t("reporting_lab.descriptions.marketing.text_without_profit", {defaultValue: "Souhrn placených i neplacených kanálů: náklady, hodnota objednávek, ROAS, PNO a rozpad marketingového výkonu bez marže a nákupních cen."})}
                </ReportingPageDescription>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.kpi.marketing_spend", {defaultValue: "Marketingové náklady"})} value={renderCurrencyValue(spendMoney, true)} meta={t("reporting_lab.marketing.system_count", {defaultValue: "{{count}} systémů", count: channelRows.length})}/>
                </div>
                {hasPoas && <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.kpi.conversion_value", {defaultValue: "Hodnota objednávek"})} value={renderCurrencyValue(turnoverMoney, true)} meta={t("reporting_lab.marketing.order_count", {defaultValue: "{{count}} objednávek", count: renderNumber(totals.orders)})}/>
                </div>}
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard
                        loading={loading}
                        label="PNO"
                        value={<span className="flex flex-wrap items-center gap-2">
                            <span>{renderPercentValue(pno)}</span>
                            <span className="rounded-md bg-violet-100 px-2 py-1 text-xs font-semibold text-violet-700 dark:bg-violet-950/60 dark:text-violet-200">
                                ROAS {renderRatioValue(roas)}
                            </span>
                        </span>}
                        meta={t("reporting_lab.metrics_help.pno", {defaultValue: "Marketingové náklady jako podíl přiřazeného obratu."})}
                    />
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard
                        loading={loading}
                        label="POAS"
                        value={<span className="flex flex-wrap items-center gap-2">
                            <span>{renderRatioValue(poas)}</span>
                            <span className="rounded-md bg-violet-100 px-2 py-1 text-xs font-semibold text-violet-700 dark:bg-violet-950/60 dark:text-violet-200">
                                {t("reporting_lab.table.cost_per_order", {defaultValue: "Cena / obj."})} {renderCurrencyValue(cpa, true)}
                            </span>
                        </span>}
                        meta={t("reporting_lab.metrics_help.poas", {defaultValue: "Marže zboží před marketingem dělená marketingovými náklady."})}
                    />
                </div>

                <Panel loading={loading} title={t("reporting_lab.marketing.channel_comparison", {defaultValue: "Srovnání reklamních systémů"})} className="col-span-12">
                    <EChart option={marketingComparisonOption} className="h-96"/>
                </Panel>

                <div className="col-span-12 grid grid-cols-12 gap-4">
                    {channelRows.slice(0, 4).map((row, index) => <div key={row.channel.id} className="col-span-12 md:col-span-6 xl:col-span-3">
                        <MarketingSystemCard
                            row={row}
                            rows={channelRows}
                            color={ChannelMixColors[index % ChannelMixColors.length]}
                            renderCurrencyValue={renderCurrencyValue}
                            renderNumber={renderNumber}
                            renderPercentValue={renderPercentValue}
                            renderRatioValue={renderRatioValue}
                            hasPoas={hasPoas}
                        />
                    </div>)}
                </div>

                <Panel
                    loading={loading}
                    title={t("reporting_lab.marketing.system_table", {defaultValue: "Výkon reklamních systémů"})}
                    className="col-span-12"
                >
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                {tableColumns.map((column) => <SortableHeader key={column.key} label={column.label} tooltip={column.tooltip} sortKey={column.key} sort={tableSort} onSort={toggleSort} align={column.align ?? "right"}/>) }
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {sortedTableRows.map((row) => <tr key={row.channel.id}>
                                {tableColumns.map((column) => <td key={column.key} className={`${column.align === "left" ? "" : "text-right"} text-slate-600 dark:text-slate-300`}>{column.render(row)}</td>)}
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>
    </WideSecureLayout>;
}
